import { Action } from 'redux';
import { ApiHeaderOpts } from 'services/api';

export { isLoading, doneLoading } from 'store/config/actions';

export const EDIT_DEFAULT_LETTER = 'letter-template/EDIT_DEFAULT_LETTER';
export const RESET_LETTER_CURRENT = 'letter-template/RESET_LETTER_CURRENT';
export const PERSIST_DEFAULT_LETTER = 'letter-template/PERSIST_DEFAULT_LETTER';
export const PREVIEW_LETTER_TEMPLATE = 'letter-template/PREVIEW_LETTER_TEMPLATE';

export const CREATE_LETTER_TEMPLATE = 'letter-template/CREATE_LETTER';
export const GET_LETTER_TEMPLATES = 'letter-template/GET_ALL_LETTER_TEMPLATES';
export const GET_LETTER_TEMPLATE = 'letter-template/GET_LETTER_TEMPLATES';
export const GET_LETTER_TEMPLATE_OPTIONS = 'letter-template/GET_LETTER_TEMPLATE_OPTIONS';
export const PERSIST_LETTER_TEMPLATE = 'letter-template/PERSIST_LETTER_CURRENT';
export const PERSIST_LETTER_TEMPLATES = 'letter-templates/PERSIST_LETTER_TEMPLATES';
export const PERSIST_LETTER_TEMPLATE_OPTIONS = 'letter-template PERSIST_LETTER_TEMPLATE_OPTIONS';
export const UPDATE_LETTER_TEMPLATE = 'letter-template/EDIT_LETTER_TEMPLATE';
export const DELETE_LETTER_TEMPLATE = 'letter-template/DELETE_LETTER_TEMPLATE';

// No need to export
interface LetterTemplatePayload<T extends LetterTemplate = LetterTemplate> {
  readonly type: LetterTemplateType;
  readonly template: T;
}

export interface GetLetterTemplateOptionsAction extends Action<typeof GET_LETTER_TEMPLATE_OPTIONS> {
  readonly payload: {
    readonly type: LetterTemplateType;
  };
}

export interface PersistLetterTemplateOptionsAction extends Action<typeof PERSIST_LETTER_TEMPLATE_OPTIONS> {
  readonly payload: {
    readonly type: LetterTemplateType;
    readonly options: LetterOptions;
  };
}

export interface PreviewLetterTemplateAction<T extends LetterTemplate = LetterTemplate>
  extends Action<typeof PREVIEW_LETTER_TEMPLATE>, OnCompleteAction {
  readonly payload: {
    readonly type: LetterTemplateType;
    readonly template: T;
    readonly format: 'pdf';
  };
}

// All actions related to saving a new letter
export interface CreateLetterTemplateAction<T extends LetterTemplate = LetterTemplate>
  extends Action<typeof CREATE_LETTER_TEMPLATE>, OnCompleteAction {
  readonly payload: LetterTemplatePayload<T>;
}

// All actions related to deleting an letter
export interface DeleteLetterTemplateAction<T extends LetterTemplate = LetterTemplate>
  extends Action<typeof DELETE_LETTER_TEMPLATE>, OnCompleteAction {
  readonly payload: LetterTemplatePayload<T>;
}

// All actions related to editing an letter
export interface UpdateLetterTemplateAction<T extends LetterTemplate = LetterTemplate>
  extends Action<typeof UPDATE_LETTER_TEMPLATE>, OnCompleteAction {
  readonly payload: LetterTemplatePayload<T>;
}

export interface EditDefaultLetterTemplateAction<T extends LetterTemplate = LetterTemplate>
  extends Action<typeof EDIT_DEFAULT_LETTER>, OnCompleteAction {
  readonly payload: LetterTemplatePayload<T>;
}

export interface PersistDefaultLetterAction<T extends LetterTemplate = LetterTemplate> extends Action<
  typeof PERSIST_DEFAULT_LETTER
> {
  readonly payload: LetterTemplatePayload<T>;
}

/**
 * Lookup an Letter Template by type
 */
export interface GetLetterTemplateAction extends Action<typeof GET_LETTER_TEMPLATE> {
  readonly payload: {
    readonly type: LetterTemplateType;
    readonly id: number;
  };
}

/**
 * Persist template to redux with opts
 */
export interface PersistCurrentLetterTemplateAction<T extends LetterTemplate = LetterTemplate> extends Action<
  typeof PERSIST_LETTER_TEMPLATE
> {
  readonly payload: {
    readonly type: LetterTemplateType;
    readonly template: T;
    readonly opts: ApiHeaderOpts;
  };
}

export interface GetLetterTemplatesAction extends Action<typeof GET_LETTER_TEMPLATES> {
  readonly payload: {
    readonly type: LetterTemplateType;
  };
}

export interface PersistLetterTemplatesAction<T extends LetterTemplate = LetterTemplate> extends Action<
  typeof PERSIST_LETTER_TEMPLATES
> {
  readonly payload: {
    readonly type: LetterTemplateType;
    readonly templates: ReadonlyArray<T>;
  };
}

// Letter Templates
export type LetterTemplateAction =
  | CreateLetterTemplateAction
  | DeleteLetterTemplateAction
  | EditDefaultLetterTemplateAction
  | GetLetterTemplateAction
  | GetLetterTemplatesAction
  | GetLetterTemplateOptionsAction
  | PersistCurrentLetterTemplateAction
  | PersistLetterTemplatesAction
  | PersistLetterTemplatesAction
  | PersistLetterTemplateOptionsAction
  | UpdateLetterTemplateAction;

//
// --- Saga Actions ---

export const getLetterTemplateOptions = (type: LetterTemplateType): GetLetterTemplateOptionsAction => ({
  payload: {
    type,
  },
  type: GET_LETTER_TEMPLATE_OPTIONS,
});

export const persistLetterOptions = (
  type: LetterTemplateType,
  options: LetterOptions,
): PersistLetterTemplateOptionsAction => ({
  payload: {
    options,
    type,
  },
  type: PERSIST_LETTER_TEMPLATE_OPTIONS,
});

export const previewLetterTemplate = (
  type: LetterTemplateType,
  template: LetterTemplate,
  onComplete?: () => void,
): PreviewLetterTemplateAction => ({
  meta: {
    onComplete,
  },
  payload: {
    format: 'pdf',
    template,
    type,
  },
  type: PREVIEW_LETTER_TEMPLATE,
});

export const createLetterTemplate = (
  type: LetterTemplateType,
  template: LetterTemplate,
  onComplete?: () => void,
): CreateLetterTemplateAction => ({
  meta: { onComplete },
  payload: {
    template,
    type,
  },
  type: CREATE_LETTER_TEMPLATE,
});

export const deleteLetterTemplate = (
  type: LetterTemplateType,
  template: LetterTemplate,
  onComplete?: () => void,
): DeleteLetterTemplateAction => ({
  meta: { onComplete },
  payload: {
    template,
    type,
  },
  type: DELETE_LETTER_TEMPLATE,
});

export const updateLetterTemplate = (
  type: LetterTemplateType,
  template: LetterTemplate,
  onComplete?: () => void,
): UpdateLetterTemplateAction => ({
  meta: { onComplete },
  payload: {
    template,
    type,
  },
  type: UPDATE_LETTER_TEMPLATE,
});

export const editDefaultLetter = (
  type: LetterTemplateType,
  template: LetterTemplate,
  onComplete?: () => void,
): EditDefaultLetterTemplateAction<LetterTemplate> => ({
  meta: {
    onComplete,
  },
  payload: {
    template,
    type,
  },
  type: EDIT_DEFAULT_LETTER,
});

export const getLetterTemplate = (type: LetterTemplateType, id: number): GetLetterTemplateAction => ({
  payload: {
    id,
    type,
  },
  type: GET_LETTER_TEMPLATE,
});

export const getLetterTemplates = (type: LetterTemplateType): GetLetterTemplatesAction => ({
  payload: {
    type,
  },
  type: GET_LETTER_TEMPLATES,
});

//
// --- Reducer Actions ---

export function persistDefaultLetter<T extends LetterTemplate = LetterTemplate>(
  type: LetterTemplateType,
  template: T,
): PersistDefaultLetterAction<T> {
  return {
    payload: {
      template,
      type,
    },
    type: PERSIST_DEFAULT_LETTER,
  };
}

export function persistLetterTemplate<T extends LetterTemplate = LetterTemplate>(
  type: LetterTemplateType,
  template: T,
  opts: ApiHeaderOpts,
): PersistCurrentLetterTemplateAction<T> {
  return {
    payload: {
      opts,
      template,
      type,
    },
    type: PERSIST_LETTER_TEMPLATE,
  };
}

export function persistLetterTemplates<T extends LetterTemplate = LetterTemplate>(
  type: LetterTemplateType,
  templates: ReadonlyArray<T>,
): PersistLetterTemplatesAction<T> {
  return {
    payload: {
      templates,
      type,
    },
    type: PERSIST_LETTER_TEMPLATES,
  };
}
